<template>
  <div v-if="user !== null" style="position: relative;">
    <v-alert
      :value="isUserBocked"
      color="red"
      icon="new_releases"
      class="block-status-alert"
    >
      Пользователь заблокирован!
    </v-alert>

    <v-alert
      :value="isUserTrusted.show"
      :color="isUserTrusted.status === 1 ? 'red' : 'green'"
      :icon="isUserTrusted.status === 2 ? 'verified_user' : 'new_releases'"
      class="trust-status-alert"
    >
      {{ isUserTrusted.status === 1 ? 'Недостоверный баланс!' : 'Баланс достоверный!' }}
    </v-alert>

    <v-snackbar v-model="showError" color="red">
      <p style="margin: 0 auto">{{ errorText }}</p>
    </v-snackbar>

    <div style="display: grid; grid-template-columns: 60% 35%; gap: 5rem">
      <div class="wrapper">
        <v-text-field
          v-model="nameInput"
          outline
          label="Ник"
          color="accent"
          class="field"
        />

        <v-text-field
          :value="emailInput"
          outline
          label="Email"
          color="accent"
          :background-color="user.verification_status === 1 ? 'success lighten-1' : null"
          class="field"
          :append-icon="User.hasAdminAccess() ? 'edit' : null"
          :readonly="!User.hasAdminAccess()"
          @click:append="shoEmailModal = true"
        >
          <template #label>
            Email <v-icon v-if="user.verification_status === 1" style="color: #4caf50 ; margin-left: 8px">
              check
            </v-icon>
          </template>
        </v-text-field>

        <v-text-field
          :value="user.social_network_accounts[0]?.link || ''"
          outline
          label="Аккаунт ВК"
          color="accent"
          readonly
          class="field"
        />

        <v-select
          v-model="status"
          :items="statuses"
          item-text="text"
          item-value="value"
          outline
          color="accent"
          label="Статус пользователя"
          class="field"
        />

        <v-text-field
          :value="balanceInput"
          outline
          readonly
          label="Баланс"
          class="field"
          color="accent"
          append-icon="edit"
          @click:append="showBalanceModal = true"
        />

        <v-text-field
          :value="`${user.accounts[0].withdrawal}/${user.accounts[0].replenishment}`"
          outline
          label="Сводная снял/пополнил"
          readonly
          color="accent"
          class="field"
        />

        <v-text-field
          :value="gameStats.matches_count"
          outline
          label="Общее количество сыгранных игр"
          readonly
          color="accent"
          class="field"
        />

        <v-text-field
          :value="gameStats.bets_sum"
          outline
          label="Общая сумма ставок"
          readonly
          color="accent"
          class="field"
        />

        <v-text-field
          v-model="referralInput"
          outline
          type="number"
          label="% по реферальной программе"
          :rules="[updatePercent]"
          color="accent"
          class="field"
        />

        <template v-if="category === 3">
          <v-text-field
            v-model="resetInput"
            outline
            type="number"
            label="Баланс для сброса категории"
            color="accent"
            class="field"
          />
        </template>

        <v-text-field
          :value="user.referrals_count"
          outline
          label="Кол-во рефералов"
          readonly
          color="accent"
          class="field"
        />

        <v-text-field
          :value="gameStats.highest_prize"
          outline
          label="Лучший выигрыш"
          readonly
          color="accent"
          class="field"
        />

        <v-select
          v-model="category"
          :items="categories"
          item-text="name_rus"
          item-value="id"
          outline
          color="accent"
          label="Категория пользователя"
          class="field"
        />
      </div>

      <v-tabs v-model="tab2">
        <v-tabs-slider color="accent" />
        <v-tab v-for="title in tabTitles2" :key="title">
          {{ title }}
        </v-tab>
        <v-tab-item>
          <v-data-table
            v-if="user.ips.length"
            :headers="[
              { text: 'IP-адрес', value: 'ip' },
              { text: 'Geo', value: 'geo' },
              { text: 'Дата', value: 'created_at' },
              { text: 'Обновлен', value: 'updated_at' }
            ]"
            :sort-by="['updated_at']"
            :pagination.sync="pagination"
            :items="user.ips"
            hide-actions
            class="ips-table"
          >
            <template #items="props">
              <td>{{ props.item.ip }}</td>
              <td>{{ props.item.geo }}</td>
              <td>{{ formatDateTime(props.item.created_at) }}</td>
              <td>{{ formatDateTime(props.item.updated_at) }}</td>
            </template>
          </v-data-table>
        </v-tab-item>

        <v-tab-item>
          <v-data-table
            v-if="user.fingerprints.length"
            :headers="[
              { text: 'Key', value: 'key' },
              { text: 'Ip', value: 'ip' },
              { text: 'Дата', value: 'created_at' },
            ]"
            :items="user.fingerprints"
            hide-actions
            class="ips-table"
          >
            <template #items="props">
              <td>{{ props.item.key }}</td>
              <td>{{ props.item.ip }}</td>
              <td>{{ formatDateTime(props.item.created_at) }}</td>
            </template>
          </v-data-table>
        </v-tab-item>
      </v-tabs>
    </div>

    <div style="display: flex; justify-content: space-between; gap: 20px; margin-bottom: 50px">
      <div>
        <span v-if="user.referrer">Реферер:
          <router-link style="color: #ff8f00" :to="`/users/${user.referrer.id}`">{{ user.referrer.name }}</router-link>
        </span>
        <span v-else>Не имеет реферера</span>

        <div class="lotteries-info">
          <v-checkbox v-model="canCreateLotteries" color="accent" />
          Может создавать розыгрыши
        </div>
      </div>

      <div style="max-width: 530px">
        <v-btn color="primary" @click="stats">Подробная статистика игр</v-btn>
        <v-btn color="primary" @click="referral">Подробно о реф. программе</v-btn>
        <v-btn color="primary" @click="resetPassword">Сбросить пароль</v-btn>
        <v-btn color="primary" :disabled="isDisabledUpdateTrust" @click="updateTrust">Сбросить расчетный баланс</v-btn>
      </div>

      <div style="width: 35%">
        <v-textarea
          v-model="commentInput"
          label="Комментарий"
          rows="1"
          box
          color="accent"
          auto-grow
        />
      </div>
    </div>

    <v-tabs v-model="tab">
      <v-tabs-slider color="accent" />

      <v-tab v-for="title in tabTitles" :key="title">
        {{ title }}
      </v-tab>

      <v-tab-item>
        <UserTransactionsTable />
      </v-tab-item>

      <v-tab-item>
        <UserTrustPointsTable />
      </v-tab-item>
    </v-tabs>

    <BalanceModal
      :value="showBalanceModal"
      :balance="balanceInput"
      @update:balanceDialog="(e) => (showBalanceModal = e)"
      @update:balanceChanged="updateBalanceAndConfirm"
      @show-error="showSnackbar"
    />

    <EmailModal
      :value="shoEmailModal"
      :email="emailInput"
      :email-verified="user.verification_status === 1"
      @update:emailDialog="(e) => (shoEmailModal = e)"
      @update:emailChanged="updateEmailAndConfirm"
      @show-error="showSnackbar"
    />

    <Base--SideButtons back :save="validate" />
  </div>
</template>

<script>
import {
  getReferralCatgories,
  getUser,
  giveRole,
  resetUserPassword,
  updateReferral,
  updateUser,
  updateUserBalance, updateUserEmail, updateUserVerification
} from '../../api/user'
import BalanceModal from '@/components/modals/BalanceModal.vue'
import EmailModal from '@/components/modals/EmailModal.vue'
import UserTransactionsTable from '@/components/common/Table/UserTransactionsTable/UserTransactionsTable.vue'
import VueMixin from '@/components/VueMixin.vue'
import UserTrustPointsTable from '@/components/common/Table/UserTrustPointsTable/UserTrustPointsTable.vue'
import { updateTrustPoints } from '../../api/trust'
import User from '../../helpers/user'
import WebSockets from '@/sockets/WebSockets'

export default {
  name: 'User',
  components: {
    UserTrustPointsTable, UserTransactionsTable, BalanceModal, EmailModal
  },
  mixins: [VueMixin],
  data () {
    return {
      isUserBocked: false,
      isUserTrusted: {
        status: 1,
        show: false
      },
      pagination: {
        sortBy: 'updated_at',
        descending: true,
        rowsPerPage: 100
      },
      showError: false,
      errorText: 'Ошибка',
      category: 0,
      status: 0,
      statuses: [
        {
          text: 'New',
          value: 0
        },
        {
          text: 'Bronze',
          value: 1
        },
        {
          text: 'Silver',
          value: 2
        },
        {
          text: 'Gold',
          value: 3
        },
        {
          text: 'Platinum',
          value: 4
        },
        {
          text: 'Diamond',
          value: 5
        }
      ],
      categories: [],
      nameInput: '',
      balanceInput: '',
      emailInput: '',
      referralInput: '',
      resetInput: '',
      commentInput: '',
      save: () => {
        if (
          this.nameInput !== this.user.name
          || this.commentInput !== this.user.comment
          || this.status !== this.user.cashback_status
        )
          this.AxiosHelperShowResponce(
            updateUser(this.user.id, {
              name: this.nameInput === this.user.name ? undefined : this.nameInput,
              comment: this.commentInput === this.user.comment ? undefined : this.commentInput,
              cashback_status: this.status === this.user.cashback_status ? undefined : this.status
            }).then(() => this.fetchUser())
          )

        if (
          this.category !== this.user.referral_programs[0].referral_category.id
          || this.referralInput !== this.user.referral_programs[0].percent
          || this.resetInput !== this.user.referral_programs[0].category_reset_border
        )
          this.AxiosHelperShowResponce(
            updateReferral(this.user.referral_programs[0].id, {
              referral_category_id:
                this.category === this.user.referral_programs[0].referral_category.id ? undefined : this.category,
              percent:
                this.referralInput === this.user.referral_programs[0].percent
                  ? undefined
                  : Number.parseFloat(this.referralInput) / 100,
              category_reset_border:
                this.resetInput === this.user.referral_programs[0].category_reset_border ? undefined : Number(this.resetInput)
            })
          )

        if (this.roleHasBeenChanged)
          this.AxiosHelperShowResponce(
            giveRole(
              this.user,
              this.prepareRolesToSave().map(x => x.name)
            ).then(() => (this.roleHasBeenChanged = false))
          )
      },
      user: null,
      gameStats: {
        bets_sum: 0,
        highest_prize: 0,
        matches_count: 0,
        referrals_count: 0
      },
      roleHasBeenChanged: false,
      canCreateLotteries: false,
      showBalanceModal: false,
      shoEmailModal: false,
      tab: null,
      tab2: null,
      tabTitles: ['История транзакций', 'Trust points'],
      tabTitles2: ['Ips', 'Fingerprints'],
      isDisabled: false
    }
  },
  computed: {
    User () {
      return User
    },
    isDisabledUpdateTrust () {
      return !(User.hasAccess() && this.isUserTrusted.show && this.isUserTrusted.status === 1 && !this.isDisabled)
    }
  },
  watch: {
    canCreateLotteries (newVal, oldVal) {
      if (newVal !== oldVal) this.roleHasBeenChanged = true
    }
  },
  created () {
    getReferralCatgories().then(res => {
      this.categories = res.data.categories
    })
    this.fetchUser()
  },
  mounted () {
    this.$emit('title_changed', `Пользователь ID: ${this.$route.params.user}`)
    WebSockets.subscribe(`user_trust_points.${this.$route.params.user}`, this.updateTrustTable)
  },
  beforeDestroy () {
    WebSockets.unsubscribe(`user_trust_points.${this.$route.params.user}`, this.updateTrustTable)
  },
  methods: {
    showSnackbar () {
      this.showError = true
      this.errorText = 'Доступно только для администратора'
    },
    fetchUser () {
      getUser(this.$route.params.user).then(response => {
        this.isDisabled = false
        this.user = response.data
        if (this.user.gaming_user_statistics.length !== 0) this.gameStats = this.user.gaming_user_statistics[0]

        if (this.user.referral_programs.length !== 0) {
          this.user.referral_programs[0].percent = (this.user.referral_programs[0].percent * 100).toString()
          this.category = this.user.referral_programs[0].referral_category.id
        }
        this.user.accounts[0].value = parseFloat(this.user.accounts[0].value).toFixed(2)
        this.status = this.user.cashback_status
        this.canCreateLotteries = this.user.roles.findIndex(x => x.id === 4) > -1
        this.nameInput = this.user.name
        this.balanceInput = this.user.accounts[0].value
        this.emailInput = this.user.email
        this.referralInput = this.user.referral_programs[0].percent
        this.resetInput = this.user.referral_programs[0].category_reset_border
        this.commentInput = this.user.comment
        this.isUserBocked = !this.user.roles.length > 0
        if (this.user.trust_points.length > 0 && this.user.trust_points[0].status !== 0) {
          this.isUserTrusted.status = this.user.trust_points[0].status
          this.isUserTrusted.show = true
        }
      })
    },
    prepareRolesToSave () {
      const rolesWithoutLotteryCreator = this.user.roles.filter(x => x.id != 4)
      if (!this.canCreateLotteries) return rolesWithoutLotteryCreator
      return [...rolesWithoutLotteryCreator, { id: 4, name: 'lottery_creator' }]
    },
    referral () {
      this.$router.push(`/users/${this.user.id}/referal`)
    },
    validate () {
      if (this.referralInput >= 2 && this.referralInput <= 10) this.save()
    },
    resetPassword () {
      this.AxiosHelperShowResponce(
        resetUserPassword(this.user.id, {
          password: '123456789'
        })
      )
    },
    updateTrust () {
      this.isDisabled = true
      updateTrustPoints(this.user.trust_points[0].id).then(() => {
        this.fetchUser()
        EventBus.$emit('update-user-trust_point-table')
      })
    },
    updateTrustTable () {
      this.fetchUser()
      EventBus.$emit('update-user-trust_point-table')
    },
    updatePercent (value) {
      if (value < 2) return '< 2'
      if (value > 10) return '> 10'
      return true
    },
    stats () {
      this.$router.push(`/users/${this.user.id}/stats`)
    },
    updateBalanceAndConfirm (v) {
      if (v.balance) this.balanceInput = v.balance
      updateUserBalance(this.user.id, {
        amount: v.amount ? v.amount : this.balanceInput,
        direction: v.direction ? v.direction : undefined
      })
        .then(() => {
          this.fetchUser()
          EventBus.$emit('update-user-transaction-table')
        })
        .catch(e => {
          this.showError = true
          this.errorText = e.response.data.message
        })
    },
    updateEmailAndConfirm (v) {
      let promise
      if (v.email)
        promise = () => updateUserEmail(this.user.id, { email: v.email })
      else if (v.verification)
        promise = () => updateUserVerification(this.user.id)

      promise()
        .then(() => {
          this.fetchUser()
        })
        .catch(e => {
          this.showError = true
          this.errorText = e.response.data.message
        })
    }
  }
}
</script>

<style lang="scss" scoped>
.block-status-alert {
  position: absolute;
  height: 52px;
  left: 280px;
  top: -73px;
}

.trust-status-alert {
  position: absolute;
  height: 52px;
  left: 565px;
  top: -73px;
}

.field {
  width: 20%;
}
.lotteries-info {
  display: flex;
  align-items: center;
  width: 320px;
  font-size: 20px;
}
.ips-table {
  max-height: 250px;
  overflow: auto;
}
.wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  position: relative;
}
</style>
